<template>
  <div class="receipt-upload">
    <input
      ref="fileInput"
      type="file"
      class="receipt-upload__file-input"
      accept=".pdf,image/jpeg,image/png"
      @change="onFileSelected"
    />
    <BlueButton
      class="receipt-upload__btn"
      :is-disabled="isDisabled"
      :is-loading="isUploading"
      @click="openFilePicker"
    >
      Прикріпити файл квитанції
    </BlueButton>

    <p
      v-if="uploadError"
      class="receipt-upload__hint receipt-upload__hint--error"
    >
      Помилка при завантаженні квитанції. Спробуйте ще раз.
    </p>
    <div
      v-else-if="receiptLoaded && uploadedFileName"
      class="receipt-upload__file-row"
    >
      <span class="receipt-upload__filename">
        <FileIcon class="receipt-upload__file-icon" />
        {{ uploadedFileName }}
      </span>
      <button
        class="receipt-upload__remove-btn"
        @click="removeFile"
      >
        <CloseIcon color="#717680" />
      </button>
    </div>
  </div>
</template>

<script>
import { uploadReceipt } from '@/api/invoice';
import { invoiceTypes } from '@/entities/insurance/config';
import { BlueButton } from '@/shared/ui/form';
import { CloseIcon, FileIcon } from '@/shared/ui/icons';

export default {
  components: { BlueButton, CloseIcon, FileIcon },
  props: {
    payments: { type: Array, default: () => [] },
    isDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      isUploading: false,
      uploadError: false,
      receiptLoaded: false,
      uploadedFileName: '',
      manuallyRemoved: false,
    };
  },
  watch: {
    payments: {
      immediate: true,
      handler() {
        this.checkExistingReceipt();
      },
    },
  },
  mounted() {
    this.checkExistingReceipt();
  },
  methods: {
    checkExistingReceipt() {
      if (this.receiptLoaded || this.manuallyRemoved) return;
      const payment = this.payments?.find(
        item => item.invoice?.type_id === invoiceTypes.LEGAL_ENTITY_INSURANCE_PAYMENT
      );
      if (payment?.invoice?.receipt) {
        this.receiptLoaded = true;
        const parts = payment.invoice.receipt.split('/');
        this.uploadedFileName = parts[parts.length - 1];
        this.$emit('uploaded');
      }
    },
    openFilePicker() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    removeFile() {
      this.manuallyRemoved = true;
      this.receiptLoaded = false;
      this.uploadedFileName = '';
      this.uploadError = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      this.$emit('removed');
    },
    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        this.uploadError = true;
        return;
      }

      this.uploadReceipt(file);
    },
    async uploadReceipt(file) {
      const payment = this.payments.find(
        item => item.invoice?.type_id === invoiceTypes.LEGAL_ENTITY_INSURANCE_PAYMENT
      );
      if (!payment?.invoice?.id) {
        this.uploadError = true;
        return;
      }

      this.isUploading = true;
      this.uploadError = false;

      try {
        const response = await uploadReceipt(payment.invoice.id, file);
        if (response.data?.status === 'success') {
          this.receiptLoaded = true;
          this.uploadedFileName = file.name;
          this.$emit('uploaded');
        } else {
          this.uploadError = true;
          this.$emit('error');
        }
      } catch {
        this.uploadError = true;
        this.$emit('error');
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.receipt-upload {
  width: 350px;

  @media (max-width: 768px) {
    width: 100%;
  }

  &__file-input {
    display: none;
  }

  &__btn {
    width: 100%;
    padding: 15px 85px;

    @media (max-width: 768px) {
      padding: 15px 24px;
    }
  }

  &__file-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 8px;
  }

  &__file-icon {
    margin-right: 2px;
  }

  &__filename {
    font-family: 'Geist', sans-serif;
    font-size: 14px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  &__remove-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
  }

  &__hint {
    font-family: 'Geist', sans-serif;
    font-size: 14px;
    margin: 8px 0 0;

    &--success {
      color: #2AA63C;
    }

    &--error {
      color: #FF3B30;
    }
  }
}
</style>
